import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Copy, FloppyDisk, Eye, ArrowClockwise, PaperPlaneTilt, ArrowSquareOut } from "@phosphor-icons/react";
import RichTextEditor from "@/components/RichTextEditor";

/**
 * Sits above the Quote Stage on AdminJobDetail. Two entry points:
 *   1. "Copy prompt for ChatGPT" — no LLM cost, admin uses external ChatGPT
 *   2. "Generate with Claude" — one-click Claude Sonnet 4.6 via Emergent LLM key
 * Output lands in a WYSIWYG editor. Save persists to job.quote_draft. Copy HTML
 * puts the raw HTML on the clipboard for pasting into the external proposal tool.
 */
export default function QuoteDraftPanel({ jobId, initialHtml, initialBpUrl, onSaved }) {
  const [html, setHtml] = useState(initialHtml || "");
  const [prompt, setPrompt] = useState(null); // {system_prompt, user_prompt}
  const [promptOpen, setPromptOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [bpUrl, setBpUrl] = useState(initialBpUrl || "");

  useEffect(() => { setHtml(initialHtml || ""); }, [initialHtml]);
  useEffect(() => { setBpUrl(initialBpUrl || ""); }, [initialBpUrl]);

  const dirty = html !== (initialHtml || "");

  const openPrompt = async () => {
    try {
      const { data } = await api.get(`/admin/jobs/${jobId}/quote-prompt`);
      setPrompt(data);
      setPromptOpen(true);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Failed to build prompt"); }
  };

  const generate = async () => {
    if (html && !window.confirm("Replace the current draft with a fresh AI draft?")) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/jobs/${jobId}/generate-quote-draft`);
      setHtml(data.html);
      toast.success("Draft ready — review and edit before posting");
      onSaved?.();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "AI draft failed"); }
    finally { setGenerating(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/jobs/${jobId}/quote-draft`, { html });
      toast.success("Draft saved");
      onSaved?.();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const copyHtml = async () => {
    try {
      await navigator.clipboard.writeText(html);
      toast.success("HTML copied — paste into your proposal editor");
    } catch { toast.error("Copy failed — select and copy manually"); }
  };

  const copyPromptFull = async () => {
    if (!prompt) return;
    const text = `--- SYSTEM PROMPT ---\n${prompt.system_prompt}\n\n--- USER PROMPT ---\n${prompt.user_prompt}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Prompt copied — paste into ChatGPT");
    } catch { toast.error("Copy failed — select and copy manually"); }
  };

  const pushToBp = async () => {
    setPushing(true);
    try {
      const { data } = await api.post(`/admin/jobs/${jobId}/push-to-betterproposals`);
      setBpUrl(data.url || "");
      const unreg = (data.unregistered_tags || []).length;
      const msg = unreg
        ? `Proposal created (${unreg} unregistered tag${unreg > 1 ? 's' : ''} skipped)`
        : "Proposal created — opening…";
      toast.success(msg);
      if (data.url) window.open(data.url, "_blank", "noopener,noreferrer");
      onSaved?.();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "BetterProposals push failed");
    } finally { setPushing(false); }
  };

  return (
    <section className="sticker p-6" data-testid="quote-draft-panel">
      <div className="chip mb-3">// AI quote draft</div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h3 className="font-display text-2xl mb-1">Introduction · Goals · Design & Branding</h3>
          <p className="text-sm text-[var(--gc-text-muted)] max-w-xl">
            One-click AI draft using the customer&apos;s questionnaire. <strong>Create in BetterProposals</strong> spins up a proposal from your template with the customer attached — then paste each section&apos;s HTML into the proposal body in BP.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={openPrompt} className="btn-secondary inline-flex items-center gap-2 px-4 py-2 text-sm" data-testid="quote-copy-prompt-btn">
            <Copy size={14} weight="bold" /> Copy prompt for ChatGPT
          </button>
          <button onClick={generate} disabled={generating} className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50" data-testid="quote-generate-btn">
            <Sparkle size={14} weight="bold" /> {generating ? "Drafting…" : html ? "Regenerate" : "Generate with Claude"}
          </button>
          <button onClick={pushToBp} disabled={pushing} className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[var(--gc-navy)] text-white font-semibold border-2 border-[var(--gc-navy)] hover:bg-[var(--gc-orange)] hover:border-[var(--gc-orange)] transition-colors disabled:opacity-50" data-testid="quote-push-bp-btn">
            <PaperPlaneTilt size={14} weight="bold" /> {pushing ? "Creating…" : "Create in BetterProposals"}
          </button>
        </div>
      </div>
      {bpUrl && (
        <div className="mb-4 flex items-center gap-2 text-xs bg-[var(--gc-cream,#fdf8f0)] border border-[var(--gc-border)] px-3 py-2" data-testid="quote-bp-link-banner">
          <span className="tech-label">// BetterProposals proposal:</span>
          <a href={bpUrl} target="_blank" rel="noreferrer" className="font-mono-tech text-[var(--gc-orange)] hover:underline break-all inline-flex items-center gap-1">
            {bpUrl} <ArrowSquareOut size={12} weight="bold" />
          </a>
        </div>
      )}

      {html ? (
        <>
          <RichTextEditor value={html} onChange={setHtml} placeholder="AI draft will appear here…" />
          <div className="flex flex-wrap gap-2 mt-3">
            <button onClick={save} disabled={saving || !dirty} className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-40" data-testid="quote-draft-save">
              <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : dirty ? "Save draft" : "Saved"}
            </button>
            <button onClick={copyHtml} className="btn-secondary inline-flex items-center gap-2 px-4 py-2 text-sm" data-testid="quote-draft-copy-html">
              <Copy size={14} weight="bold" /> Copy HTML
            </button>
            <button onClick={() => setPreviewOpen(true)} className="btn-secondary inline-flex items-center gap-2 px-4 py-2 text-sm" data-testid="quote-draft-preview">
              <Eye size={14} weight="bold" /> Preview
            </button>
            <button onClick={generate} disabled={generating} className="text-sm text-[var(--gc-text-muted)] link-underline inline-flex items-center gap-1.5 ml-auto disabled:opacity-40" data-testid="quote-draft-regenerate">
              <ArrowClockwise size={14} weight="bold" /> Regenerate
            </button>
          </div>
        </>
      ) : (
        <div className="border border-dashed border-[var(--gc-border)] p-8 text-center text-[var(--gc-text-muted)] text-sm">
          No draft yet. Click <strong>Generate with Claude</strong> to create one from the questionnaire, or <strong>Copy prompt for ChatGPT</strong> to work outside the app.
        </div>
      )}

      {/* Prompt modal */}
      {promptOpen && prompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="quote-prompt-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPromptOpen(false)} />
          <div className="relative bg-white max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--gc-border)]">
              <div>
                <div className="eyebrow mb-1">ChatGPT prompt · markdown mode</div>
                <div className="font-display text-xl">Copy the block below into ChatGPT</div>
              </div>
              <button onClick={() => setPromptOpen(false)} className="text-sm link-underline" data-testid="quote-prompt-close">Close</button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 font-mono-tech text-xs whitespace-pre-wrap leading-relaxed">
              <div className="font-semibold text-[var(--gc-orange)] mb-2">--- SYSTEM PROMPT ---</div>
              {prompt.system_prompt}
              <div className="font-semibold text-[var(--gc-orange)] mb-2 mt-6">--- USER PROMPT ---</div>
              {prompt.user_prompt}
            </div>
            <div className="border-t border-[var(--gc-border)] px-6 py-4 flex items-center gap-3">
              <button onClick={copyPromptFull} className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm" data-testid="quote-prompt-copy">
                <Copy size={14} weight="bold" /> Copy full prompt
              </button>
              <span className="text-xs text-[var(--gc-text-muted)]">Paste this into ChatGPT. Its reply arrives as 3 clearly-delimited sections. Copy each section from the ChatGPT UI (not the raw markdown) — the styling comes through when you paste into BetterProposals.</span>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal — renders the current HTML as it would appear */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="quote-preview-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPreviewOpen(false)} />
          <div className="relative bg-white max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--gc-border)]">
              <div className="font-display text-xl">Preview</div>
              <button onClick={() => setPreviewOpen(false)} className="text-sm link-underline" data-testid="quote-preview-close">Close</button>
            </div>
            <div className="flex-1 overflow-y-auto p-8">
              <div className="rte-content prose max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
